import React from 'react';
import { Printer, MapPin, List, Server } from 'lucide-react';
import { PrinterDto } from '../../../services/api';
import { PrinterStatusBadge } from './PrinterStatusBadge';
import { AgentStatusIndicator } from './AgentStatusIndicator';

interface PrinterCardProps {
  printer: PrinterDto;
  onViewQueue: (printerId: string, printerName: string) => void;
}

export const PrinterCard: React.FC<PrinterCardProps> = ({ printer, onViewQueue }) => {
  return (
    <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 shadow-sm hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Printer size={28} className="text-green-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-slate-900 leading-tight mb-1">
              {printer.name || 'Sin Nombre'}
            </h3>
            <div className="flex items-center gap-2 text-slate-500">
              <MapPin size={16} />
              <span className="text-sm font-medium">{printer.location || 'Sin ubicación'}</span>
            </div>
          </div>
        </div>
        <PrinterStatusBadge status={printer.overallStatus} />
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-slate-50 p-3 rounded-lg">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Tipo</p>
          <p className="font-medium text-slate-700">{printer.type || '-'}</p>
        </div>
        <div className="bg-slate-50 p-3 rounded-lg">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Agentes</p>
          <div className="flex items-center gap-2">
            <Server size={16} className="text-slate-400" />
            <span className="font-medium text-slate-700">{printer.agents?.length || 0}</span>
          </div>
        </div>
      </div>

      {/* Capabilities */}
      {printer.capabilities && (
        <div className="mb-6">
          <div className="flex flex-wrap gap-2">
            <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold ${
              printer.capabilities.color ? 'bg-pink-100 text-pink-700' : 'bg-slate-100 text-slate-700'
            }`}>
              {printer.capabilities.color ? 'Color' : 'B&N'}
            </span>
            {printer.capabilities.duplex && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-green-100 text-green-700">
                Duplex
              </span>
            )}
            {printer.capabilities.maxResolutionDpi && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-green-100 text-green-700">
                {printer.capabilities.maxResolutionDpi} DPI
              </span>
            )}
          </div>
        </div>
      )}

      {/* Footer / Actions */}
      <button 
        onClick={() => onViewQueue(printer.id || '', printer.name || 'Impresora')}
        className="w-full flex items-center justify-center gap-2 px-4 py-4 bg-green-600 hover:bg-green-700 active:bg-green-800 text-white rounded-xl text-lg font-bold transition-all shadow-lg shadow-green-200 active:scale-[0.98]"
      >
        <List size={24} />
        Ver Cola de Impresión
      </button>
    </div>
  );
};
